import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import "./WarehouseLocationInventoryDetailPanel.css";
// WAREHOUSE_LOCATION_HIDE_ZERO_STOCK_ROWS_V1

function norm(value) {
  return String(value || "").trim().toUpperCase();
}

function num(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function money(value) {
  return `${Math.round(num(value)).toLocaleString()} Ks`;
}

// Inventory test view uses real catalog articles only. It is a presentation
// filter; it never deletes or changes stock movements.
const INVENTORY_TEST_ARTICLE_CODES = new Set([
  "10539-031-002-70",
  "10539-030-001-01",
  "10529-131-002-70",
  "10527-032-072-70",
  "HMMT31161",
  "3482R",
]);

function normalizeRow(row) {
  const qty = num(
    row.qty ??
      row.quantity ??
      row.erpQty ??
      row.erp_qty ??
      row.stock_qty ??
      row.balance
  );

  return {
    locationCode: norm(row.locationCode || row.location_code),
    brand: norm(row.brand || "UNKNOWN"),
    productCode: norm(row.productCode || row.product_code || row.code),
    color: norm(row.color || row.colour || "-"),
    size: String(row.size || "-").trim(),
    qty,
    price: num(row.price || row.salePrice || row.sale_price || row.unit_price),
  };
}

export default function WarehouseLocationInventoryDetailPanel({
  user,
  locationCode,
}) {
  const cleanLocation = norm(locationCode);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [positiveOnly, setPositiveOnly] = useState(false);
  const testView = true;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadInventory() {
    if (!user?.username || !cleanLocation) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        username: user.username,
        location: cleanLocation,
        brand: "ALL",
        search,
        positiveOnly: String(positiveOnly),
        stockScope: "TRANSFER_TEST",
        limit: "5000",
      });

      const response = await fetch(
        `${API_BASE}/api/erp/warehouse/inventory?${params.toString()}`,
        { cache: "no-store" }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Inventory load failed");
      }

      const list = Array.isArray(data.rows) ? data.rows : [];
      setRows(list.map(normalizeRow).filter((row) => row.productCode && Number(row.qty || 0) !== 0));
    } catch (err) {
      setRows([]);
      setError(err.message || "Inventory load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, cleanLocation, positiveOnly]);

  const filteredRows = useMemo(() => {
    const q = norm(search);

    return rows.filter((row) => {
      if (testView && !INVENTORY_TEST_ARTICLE_CODES.has(row.productCode)) {
        return false;
      }

      if (!q) return true;

      return (
        row.productCode.includes(q) ||
        row.color.includes(q) ||
        row.size.includes(q) ||
        row.brand.includes(q)
      );
    });
  }, [rows, search, testView]);

  const allSizes = useMemo(() => {
    return Array.from(new Set(filteredRows.map((row) => row.size))).sort(
      (a, b) => Number(a) - Number(b) || String(a).localeCompare(String(b))
    );
  }, [filteredRows]);

  const matrixRows = useMemo(() => {
    const map = new Map();

    filteredRows.forEach((row) => {
      const key = `${row.brand}|||${row.productCode}|||${row.color}`;

      if (!map.has(key)) {
        map.set(key, {
          brand: row.brand,
          productCode: row.productCode,
          color: row.color,
          price: row.price,
          sizes: {},
          total: 0,
        });
      }

      const target = map.get(key);
      target.sizes[row.size] = num(target.sizes[row.size]) + row.qty;
      target.total += row.qty;

      if (!target.price && row.price) {
        target.price = row.price;
      }
    });

    return Array.from(map.values()).filter((row) => Number(row.total || 0) !== 0).sort(
      (a, b) =>
        String(a.brand).localeCompare(String(b.brand)) ||
        String(a.productCode).localeCompare(String(b.productCode), undefined, {
          numeric: true,
        }) ||
        String(a.color).localeCompare(String(b.color))
    );
  }, [filteredRows]);

  const summary = useMemo(() => {
    const totalQty = filteredRows.reduce((sum, row) => sum + row.qty, 0);
    const negative = filteredRows.filter((row) => row.qty < 0).length;
    const value = filteredRows.reduce(
      (sum, row) => sum + row.qty * row.price,
      0
    );

    return {
      skuRows: matrixRows.length,
      totalQty,
      negative,
      value,
    };
  }, [filteredRows, matrixRows.length]);

  return (
    <div className="warehouseLocationInventory">
      <div className="warehouseLocationInventory__head">
        <div>
          <span>LOCATION INVENTORY</span>
          <h2>{cleanLocation} Inventory</h2>
          <p>{cleanLocation} transfer test stock only. POS sales are excluded.</p>
        </div>

        <button type="button" onClick={loadInventory} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="warehouseLocationInventory__error">{error}</div>
      )}

      <div className="warehouseLocationInventory__filters">
        <label>
          <span>Search</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="product code / color / size"
          />
        </label>

        <label className="warehouseLocationInventory__check">
          <input
            type="checkbox"
            checked={positiveOnly}
            onChange={(e) => setPositiveOnly(e.target.checked)}
          />
          <span>Positive stock only</span>
        </label>

        <button type="button" onClick={loadInventory}>
          Search
        </button>
      </div>

      {testView && (
        <div className="warehouseLocationInventory__testNote">
          Test view shows six real catalog article numbers for quick transfer testing.
          POS sale movements are excluded from this transfer test view.
          <div className="warehouseLocationInventory__testCodes">
            {Array.from(INVENTORY_TEST_ARTICLE_CODES).map((code) => (
              <code key={code}>{code}</code>
            ))}
          </div>
        </div>
      )}

      <div className="warehouseLocationInventory__summary">
        <div>
          <span>SKU Rows</span>
          <b>{summary.skuRows}</b>
        </div>

        <div>
          <span>Total Qty</span>
          <b>{summary.totalQty}</b>
        </div>

        <div>
          <span>Value</span>
          <b>{money(summary.value)}</b>
        </div>

        <div>
          <span>Negative</span>
          <b className={summary.negative > 0 ? "isBad" : ""}>
            {summary.negative}
          </b>
        </div>
      </div>

      <div className="warehouseLocationInventory__tableWrap">
        <table>
          <thead>
            <tr>
              <th>Brand</th>
              <th>Code</th>
              <th>Color</th>
              {allSizes.map((size) => (
                <th key={size}>{size}</th>
              ))}
              <th>Total</th>
              <th>Price</th>
            </tr>
          </thead>

          <tbody>
            {matrixRows.length === 0 ? (
              <tr>
                <td colSpan={5 + allSizes.length}>No inventory found.</td>
              </tr>
            ) : (
              matrixRows.map((row) => (
                <tr key={`${row.brand}-${row.productCode}-${row.color}`}>
                  <td>{row.brand}</td>
                  <td className="warehouseLocationInventory__code">
                    {row.productCode}
                  </td>
                  <td>{row.color}</td>
                  {allSizes.map((size) => {
                    const qty = num(row.sizes[size]);
                    return (
                      <td
                        key={size}
                        className={
                          qty < 0
                            ? "qtyBad"
                            : qty > 0
                            ? "qtyGood"
                            : ""
                        }
                      >
                        {qty || ""}
                      </td>
                    );
                  })}
                  <td>{row.total}</td>
                  <td>{money(row.price)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
